import React, { useState } from "react";
import { X, Share2, Send, Copy, Loader2, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * P6.1 · Share bottom sheet.
 * Records a share event via POST /api/social/share and, when the browser
 * supports it, invokes the native share menu. NEVER creates a transaction.
 */
export default function ShareSheet({ open, objectType, objectId, title, onClose }) {
  const [message, setMessage] = useState("");
  const [saving, setSaving]   = useState(false);

  if (!open) return null;

  const externalUrl = () => {
    const base = window.location.origin;
    if (objectType === "auction") return `${base}/live-world/auction/${objectId}`;
    if (objectType === "order")   return `${base}/transactions/order/${objectId}`;
    return `${base}/live-world?focus=${objectId}`;
  };

  const share = async ({ useNative } = {}) => {
    setSaving(true);
    try {
      // Record on the server first (idempotency-free — one row per press
      // is intentional so share_count increments).
      await api.post("/social/share", {
        object_type: objectType, object_id: objectId, message,
      });
      const url = externalUrl();
      if (useNative && navigator.share) {
        try { await navigator.share({ title: title || "Find2near", text: message, url }); }
        catch { /* user dismissed */ }
      } else {
        await navigator.clipboard?.writeText(url);
        toast.success("Link copied to clipboard");
      }
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't share");
    } finally { setSaving(false); }
  };

  const shareToStory = async () => {
    setSaving(true);
    try {
      await api.post("/social/share/to-story", {
        object_type: objectType, object_id: objectId, text: message,
      });
      toast.success("Posted to your 24 h Story");
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post story");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-end" onClick={onClose} data-testid="share-sheet">
      <div className="w-full bg-white rounded-t-3xl p-5 mx-auto max-w-2xl space-y-3"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase font-black text-stone-500">Share</div>
            <h3 className="text-lg font-black text-stone-900 truncate">{title || "Find2near item"}</h3>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-stone-100 grid place-items-center" data-testid="share-close">
            <X className="w-4 h-4"/>
          </button>
        </div>
        <textarea
          data-testid="share-message"
          rows={3} placeholder="Add a note (optional)"
          className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm"
          value={message} onChange={(e) => setMessage(e.target.value)}
        />
        <div className="grid grid-cols-3 gap-2">
          <button data-testid="share-copy" disabled={saving}
                  onClick={() => share({ useNative: false })}
                  className="py-2.5 rounded-full bg-stone-100 text-stone-800 font-black text-xs flex items-center justify-center gap-1 disabled:opacity-50">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Copy className="w-3.5 h-3.5"/>}
            Copy link
          </button>
          <button data-testid="share-story" disabled={saving}
                  onClick={shareToStory}
                  className="py-2.5 rounded-full bg-purple-600 text-white font-black text-xs flex items-center justify-center gap-1 disabled:opacity-50">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Zap className="w-3.5 h-3.5"/>}
            Story
          </button>
          <button data-testid="share-native" disabled={saving}
                  onClick={() => share({ useNative: true })}
                  className="py-2.5 rounded-full bg-[#E05E36] text-white font-black text-xs flex items-center justify-center gap-1 disabled:opacity-50">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Send className="w-3.5 h-3.5"/>}
            Share
          </button>
        </div>
        <p className="text-[10px] text-stone-400 italic">
          Sharing does not create an order, offer, or payment.
        </p>
      </div>
    </div>
  );
}

/** Trigger button that opens a ShareSheet. */
export function ShareButton({ objectType, objectId, title, className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        data-testid={`share-btn-${objectType}-${objectId}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        className={`p-1 rounded-full text-stone-400 hover:bg-stone-100 ${className}`}
        aria-label="Share">
        <Share2 size={16}/>
      </button>
      <ShareSheet
        open={open} objectType={objectType} objectId={objectId} title={title}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
